import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { useBookingState } from "../hooks/useBookingState";
import { TUsePopupHook, usePopup } from "../hooks/usePopup";
import { NumPicker } from "./QuantiySelector";
import { CustomDropdown } from "./detail/CustomDropdown";
import { icQuestion } from "./image/image-src";
import { OkCancelPopup } from "./modal/OkCancelPopup";

interface QuantityCardForPassProps {
  onChange?: (quantity: number, age: "ADULT" | "CHILD") => void;
  title?: string;
  minQuantity?: number;
  className?: string;
  value?: number;
  disabled?: boolean;
}

interface DropdownOption<T extends string> {
  value: T;
  label: string;
}

interface AgeDropdownProps<T extends string> {
  value: T;
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
}

const NoticePopup = (popup: TUsePopupHook) => {
  const { t } = useTranslation();
  if (!popup.isOpen) return null;
  return (
    <OkCancelPopup
      ic={icQuestion}
      title={t(
        "When changing between adult/child, the selected items will be reset. Please add them to the cart first.",
      )}
      onOk={() => {
        // popup.onOk();
        popup.close();
      }}
      okText={t("Ok")}
    />
  );
};

function AgeDropdown<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: AgeDropdownProps<T>) {
  const bookingState = useBookingState({
    mode: "FORM",
  });
  const popupNotice = usePopup({
    onOk: (newValue) => {
      onChange(newValue as T);
      popupNotice.close();
    },
  });
  return (
    <>
      <NoticePopup {...popupNotice} />
      <CustomDropdown
        className="w-[84px]"
        disabled={disabled}
        options={options}
        value={value}
        onChange={(newValue) => {
          if (bookingState?.childrenLs?.length) {
            popupNotice.open(newValue);
          } else {
            onChange(newValue);
          }
        }}
      />
    </>
  );
}

const QuantityCardForPass: React.FC<QuantityCardForPassProps> = ({
  value,
  minQuantity = 0,
  onChange,
  className,
  disabled,
}) => {
  const { t } = useTranslation();
  const a = React.useMemo(
    () => (value && value >= minQuantity ? value : minQuantity),
    [value, minQuantity],
  );
  const [qty, setQty] = useState<number>(a);
  const [age, setAge] = useState<"ADULT" | "CHILD">("ADULT");

  useEffect(() => {
    setQty(a);
  }, [a]);

  useEffect(() => {
    if (value !== undefined && value !== qty && value >= minQuantity) {
      setQty(value);
    }
  }, [minQuantity, qty, value]);

  const ageOptions: DropdownOption<"ADULT" | "CHILD">[] = [
    { value: "ADULT", label: t("Adult") },
    { value: "CHILD", label: t("Child") },
  ];

  return (
    <div
      className={twMerge(
        "wei-1 flex w-[300px] max-w-[300px] items-center justify-between rounded-[1rem] border border-[#DCDCDC] bg-white px-4 py-[0.875rem] sm:px-5 sm:py-[0.75rem]",
        className,
        disabled ? "opacity-50" : "",
      )}
    >
      <AgeDropdown
        value={age}
        options={ageOptions}
        onChange={(newAge) => {
          setAge(newAge);
          onChange?.(0, newAge);
        }}
        disabled={disabled}
      />

      <NumPicker
        onChange={(newQty) => {
          setQty(newQty);
          onChange?.(newQty, age);
        }}
        disabled={disabled}
        minQuantity={minQuantity}
        qty={qty}
      />
    </div>
  );
};

export default QuantityCardForPass;
